import classNames from "classnames";
import _ from "lodash";
import { CSSProperties, memo, useEffect, useRef, useState } from "react";
import { useGetApi } from "src/hooks";

import { SearchableInput } from "../SearchableInput/SearchableInput";
import styles from "./LocationInput.module.scss";

// ── Types ────────────────────────────────────────────────────────────────────

export interface LocationValue {
  name: string;
  portCode: string;
  country: string;
  location: string;
}

interface PortCodeResult {
  name: string;
  country: string;
  location: string;
}

export interface LocationInputProps {
  id: string;
  value: LocationValue | string | null;
  placeholder?: string;
  readonly?: boolean;
  className?: string;
  onChange: (value: LocationValue | string) => void;
  textStyle?: CSSProperties;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const isPortCodeResult = (item: unknown): item is PortCodeResult => {
  if (typeof item !== "object" || item === null) return false;
  const parsedItem = item as PortCodeResult;
  return (
    typeof parsedItem.name === "string" &&
    typeof parsedItem.country === "string" &&
    typeof parsedItem.location === "string"
  );
};

const mapper = (data: any) => {
  if (!data?.list) return [];
  return _.uniqWith(data.list, (a, b) => _.isEqual(a, b));
};

const parseInitialValue = (
  val: LocationValue | string | null,
): LocationValue => {
  if (typeof val === "object" && val !== null) {
    return {
      name: val.name || "",
      portCode: val.portCode || "",
      country: val.country || "",
      location: val.location || "",
    };
  }
  return { name: val ?? "", portCode: "", country: "", location: "" };
};

// ── Component ────────────────────────────────────────────────────────────────

export const LocationInput = memo(function LocationInput({
  id,
  value,
  placeholder = "Search port...",
  readonly,
  className,
  onChange,
  textStyle,
}: LocationInputProps) {
  const currentVal = parseInitialValue(value);

  const [searchTerm, setSearchTerm] = useState(currentVal.name);

  const [searchResults, setSearchResults] = useState<PortCodeResult[]>([]);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const { refetch } = useGetApi({
    request: "search-port-codes",
    body: { params: { text: searchTerm } },
    fetchOnInit: true,
    mapper,
  });

  // Sync internal search term if external value changes (e.g. loaded from DB)
  useEffect(() => {
    const newVal = parseInitialValue(value);
    if (newVal.name !== searchTerm) {
      setSearchTerm(newVal.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      const fetchResults = async () => {
        try {
          const data = await refetch(
            { params: { text: searchTerm || "" } },
            true,
          );
          if (data && Array.isArray(data) && data.length > 0) {
            setSearchResults(data.filter(isPortCodeResult));
          } else {
            setSearchResults([]);
          }
        } catch {
          setSearchResults([]);
        }
      };

      void fetchResults();
    }, 300);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchTerm, refetch]);

  const handleChange = (selectedItemVal: string) => {
    setSearchTerm(selectedItemVal);

    if (!selectedItemVal) {
      onChange({ name: "", portCode: "", country: "", location: "" });
      return;
    }

    const selectedPort = searchResults.find(
      port => port.name === selectedItemVal,
    );

    if (selectedPort) {
      const portCodeStr = `${selectedPort.country} ${selectedPort.location}`;
      onChange({
        name: selectedPort.name,
        portCode: portCodeStr,
        country: selectedPort.country,
        location: selectedPort.location,
      });
    } else {
      // Keep the raw text if no exact match in results
      onChange({
        name: selectedItemVal,
        portCode: "",
        country: "",
        location: "",
      });
    }
  };

  const options = searchResults.map(port => ({
    value: port.name,
    label: `${port.name} (${port.country} ${port.location})`,
  }));

  // Add the current value if it's not in the list of results and is a COMPLETE known valid entry
  if (
    currentVal.name &&
    currentVal.portCode &&
    !options.find(o => o.value === currentVal.name)
  ) {
    options.unshift({
      value: currentVal.name,
      label: currentVal.portCode
        ? `${currentVal.name} (${currentVal.country} ${currentVal.location})`
        : currentVal.name,
    });
  }

  const displayCode = currentVal.portCode
    ? `${currentVal.country} ${currentVal.location}`.trim()
    : "";

  const inlineStyle: CSSProperties = {
    ...textStyle,
    ...(displayCode && { paddingRight: `${displayCode.length * 8 + 8}px` }),
  };

  return (
    <div
      className={classNames(
        styles.wrapper,
        readonly && styles.readonly,
        className,
      )}
      style={{
        position: "relative",
        width: "100%",
        display: "flex",
        alignItems: "center",
      }}>
      <SearchableInput
        id={id}
        value={searchTerm}
        options={options}
        placeholder={placeholder}
        readonly={readonly}
        onChange={handleChange}
        textStyle={inlineStyle}
      />
      {displayCode && (
        <div className={classNames(styles.unlocode, styles.unit)}>
          {displayCode}
        </div>
      )}
    </div>
  );
});
